import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { cn } from "src/lib/cn";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import Flex from "src/sudokit/Layout/Flex";
import { Text } from "src/sudokit/components/Text";
import CharacterPanel from "./CharacterPanel";
import WorldbuildingPanel from "./WorldbuildingPanel";
import CoverPanel from "./CoverPanel";

type Mode = "character" | "worldbuilding" | "cover";

const MODES: { id: Mode; label: string }[] = [
  { id: "character", label: "Character" },
  { id: "worldbuilding", label: "Worldbuilding" },
  { id: "cover", label: "Book Cover" },
];

const DESCRIPTIONS: Record<Mode, string> = {
  character: "The real character-portrait pipeline: fill in character/Story Bible fields, pick an art-style variant, and inspect exactly what was sent to the image model.",
  worldbuilding: "Spike: generate establishing images for a Story Bible worldbuilding entry — landscapes, cities, items, maps. Role drives the default style variant and aspect ratio.",
  cover: "Spike: generate a book-cover image (always 2:3) from a project's title, genre, synopsis, and style.",
};

const isMode = (value: string | null): value is Mode => MODES.some((m) => m.id === value);

const ImageGenTester: React.FC = () => {
  const location = useLocation();
  const history = useHistory();

  const modeParam = new URLSearchParams(location.search).get("mode");
  const mode: Mode = isMode(modeParam) ? modeParam : "character";

  const setMode = (next: Mode) => {
    const params = new URLSearchParams(location.search);
    params.set("mode", next);
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  return (
    <WithReset className="max-w-[1200px] mx-auto p-5">
      <WithReset className="flex flex-col pb-5">
        <Text as="h1" size="3xl" weight="bold">
          Image Gen Tester
        </Text>
        <Text size="sm" color="grey-1" className="max-w-[600px]">
          {DESCRIPTIONS[mode]}
        </Text>
      </WithReset>

      <Flex gap={2} className="pb-6 flex-wrap">
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMode(m.id)}
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
              m.id === mode ? "bg-app-primary-cta text-app-cta-text-color" : "bg-app-grey-4 text-app-grey-1 hover:text-app-text-color",
            )}
          >
            {m.label}
          </button>
        ))}
      </Flex>

      {mode === "character" && <CharacterPanel />}
      {mode === "worldbuilding" && <WorldbuildingPanel />}
      {mode === "cover" && <CoverPanel />}
    </WithReset>
  );
};

export default ImageGenTester;
